/**
 * 브랜드 허브 개요의 **응답 → 화면 줄** — 로그인한 상세와 공유 페이지가 같은 함수로 줄을 짓는다.
 *
 * ⚠️ 순수 함수다 — 질문은 `scope/brand-hub-overview-scope` 가 짓고, 묻는 일은 훅(브라우저)·공유 출처(서버)가 한다.
 */

import type { MomentBrandRankingResponse, MomentAnalyticsPivotResponse } from 'src/types/moments/moment-ops';

import { splitFlaggedRanking, type BrandQualityFlag } from 'src/utils/brand/brand-quality-doc';

import { toBrandPeers, type BrandPeer, BRAND_PEER_TOP } from './brand-hub-share';
import { toBrandHubRankScanNames, toBrandHubRankQualityNames } from './brand-hub-rank';

/** 품질 판정을 묻는 순위 앞 줄 수 — 막대 스물 + 빼고 채울 여유 */
export const BRAND_HUB_PEER_QUALITY_SCAN = 30;

/** 낱말 구름 한 장의 줄 수 */
export const BRAND_HUB_TAG_ROWS = 10;

/** 이름 · 건수 한 줄 */
export type BrandHubTagRow = { name: string; count: number };

/** 공식 계정(추정) — 얼굴을 찾는 재료 */
export type BrandHubOfficial = { account: string; channels: string[] };

/** 머리 요약의 기간 줄 → 언급 수 · 공식 계정 */
export function readBrandHubHead(res: MomentBrandRankingResponse | null | undefined): { mentions: number; official: BrandHubOfficial | null } {
  const row = res?.brands?.[0];
  const offi = row?.official;
  return {
    mentions: Number(row?.mentions ?? 0),
    official: offi?.account ? { account: offi.account, channels: offi.channels ?? [] } : null,
  };
}

/** 7일 줄 → 언급 수 */
export function readBrandHubCount(res: MomentBrandRankingResponse | null | undefined): number {
  return Number(res?.brands?.[0]?.mentions ?? 0);
}

/**
 * 피벗 → 낱말 줄(상위 열).
 * @param skip 뺄 이름(대소문자 무시) — 「함께 나온 브랜드」에서 자기 자신을 뺀다
 * @param hash 해시태그면 참 — 색인이 `#` 없이 담아 화면이 붙여 적는다
 */
export function toBrandHubTagRows(pivot: MomentAnalyticsPivotResponse | null | undefined, opts: { skip?: string; hash?: boolean } = {}): BrandHubTagRow[] {
  const self = String(opts.skip ?? '').trim().toLowerCase();
  return (pivot?.rows ?? [])
    .filter((row) => !self || row.key.trim().toLowerCase() !== self)
    .slice(0, BRAND_HUB_TAG_ROWS)
    .map((row) => ({ name: opts.hash && !row.key.startsWith('#') ? `#${row.key}` : row.key, count: row.total.count }));
}

/** 추이 집계 → 채널 줄(많은 순) · 가장 많은 채널 */
export function readBrandHubChannels(trend: MomentAnalyticsPivotResponse | null | undefined) {
  const channels = (trend?.rows ?? []).map((row) => ({ name: row.key, count: row.total.count })).sort((a, b) => b.count - a.count);
  return { channels, topChannel: channels[0] ?? null };
}

/**
 * 품질 판정을 물을 브랜드.
 * ⚠️ 막대 스물보다 넉넉히 묻는다 — 뺀 자리를 채우러 올라오는 줄도 판정을 받아야 한다.
 * ⚠️ 스물 밖에서 끌어올린 이 브랜드 줄의 번호는 그 위에서 빠진 만큼 당겨져야 이름 옆 「N위」(`toBrandHubRank`)와 같다.
 * @param ask `scan` 훑은 줄 전부(`toBrandHubRankScanNames`) — 머리 「N위」와 같은 훑기일 때 브라우저가 쓴다. 열쇠가 같아 한 번만 읽는다 ·
 *   `throughSelf` 이 브랜드 + 앞 `BRAND_HUB_PEER_QUALITY_SCAN` 줄 + 이 브랜드의 차례까지(`toBrandHubRankQualityNames`) — 공유 출처(서버)가 쓴다 ·
 *   `head` 이 브랜드 + 앞 줄만 — 막대 끝 번호를 비울 때(`readBrandHubPeerRanking` 의 `selfRank` 거짓)
 */
export function toBrandHubPeerQualityNames(
  name: string,
  res: MomentBrandRankingResponse | null | undefined,
  ask: 'scan' | 'throughSelf' | 'head' = 'throughSelf',
): string[] {
  if (ask === 'scan') return toBrandHubRankScanNames(res?.brands);
  const head = (res?.brands ?? []).slice(0, BRAND_HUB_PEER_QUALITY_SCAN).map((row) => row.brand);
  const through = ask === 'throughSelf' ? toBrandHubRankQualityNames(name, res?.brands) : [];
  return [...new Set([name, ...head, ...through])].filter(Boolean);
}

/**
 * 막대 끝 이 브랜드 번호를 세우나 — 막대(언급 순 훑기)가 머리 「N위」와 같은 질문일 때만 참이다.
 * ⚠️ 도달 순이거나 제품군이 걸리면 머리는 다른 훑기(도달 순 · `ranking.categoryBrands`)로 세어 두 번호가 어긋난다 — 그때는 막대 끝 번호를 비운다.
 */
export function brandHubPeerSelfRankShown(orderBy: 'mentions' | 'reach', categoryTerms: readonly string[]): boolean {
  return orderBy === 'mentions' && categoryTerms.length === 0;
}

/**
 * 상위 브랜드 막대 — 뺄 브랜드를 걷고 상위 스물 + 이 브랜드(`toBrandPeers`)로 다시 매긴다.
 * @param flagged 뺄 브랜드(`toFlaggedBrands`) — 비면 지금 그대로
 * @param selfRank 거짓이면 스물 밖에서 끌어온 이 브랜드 줄의 번호를 비운다(`brandHubPeerSelfRankShown`)
 * @returns 막대 줄 · 뺀 브랜드(스물 안에 섰을 것 + 뺀 이 브랜드)
 */
export function readBrandHubPeerRanking(
  name: string,
  res: MomentBrandRankingResponse | null | undefined,
  flagged: ReadonlyMap<string, BrandQualityFlag> = NO_FLAGS,
  selfRank = true,
): { peers: BrandPeer[]; excluded: BrandQualityFlag[] } {
  const { kept, excluded } = splitFlaggedRanking(res?.brands ?? [], (row) => row.brand, flagged, BRAND_PEER_TOP);
  const self = flagged.get(name.trim().toLowerCase());
  // 이 브랜드는 상위 밖이어도 막대 끝에 섰을 줄이다 — 뺐으면 각주에도 센다
  if (self && !excluded.some((row) => row.brand.trim().toLowerCase() === name.trim().toLowerCase())) excluded.push({ ...self, brand: name });
  return { peers: toBrandPeers(name, kept, BRAND_PEER_TOP, selfRank), excluded };
}

/** 상위 브랜드 막대의 줄만(`readBrandHubPeerRanking`) */
export function readBrandHubPeers(name: string, res: MomentBrandRankingResponse | null | undefined, flagged?: ReadonlyMap<string, BrandQualityFlag>) {
  return readBrandHubPeerRanking(name, res, flagged).peers;
}

/** 얼굴을 찾을 공식 계정 — 이웃(상세 조회) + 이 브랜드 */
export function toBrandHubOfficials(
  name: string,
  peerDetail: MomentBrandRankingResponse | null | undefined,
  official: BrandHubOfficial | null,
): { brand: string; account: string; channels: string[] }[] {
  const rows = (peerDetail?.brands ?? [])
    .map((row) => ({ brand: row.brand, account: row.official?.account ?? '', channels: row.official?.channels ?? [] }))
    .filter((row) => row.account && row.channels.length > 0);
  if (official) rows.push({ brand: name, account: official.account, channels: official.channels });
  return rows;
}

/** 광고 얼굴을 물을 브랜드 — 이 브랜드 + 이웃 전부 */
export function toBrandHubListedBrands(name: string, peerDetail: MomentBrandRankingResponse | null | undefined): string[] {
  return [name, ...(peerDetail?.brands ?? []).map((row) => row.brand)].filter(Boolean);
}

const NO_FLAGS: ReadonlyMap<string, BrandQualityFlag> = new Map();
